var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { PanelLocKeys, PanelNames } from '../UtilsClasses/FactoryConstants';
import SavedOrdersPanelTopTemplate from '@front/templates/SavedOrdersPanelTopTemplate.html';
import { Resources } from '@shared/localizations/Resources';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { LinkedPriceType } from '@shared/commons/cache/SavedOrders/SavedOrderEnums';
import { SavedOrderItem } from '../cache/SavedOrderItem';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { MathUtils } from '@shared/utils/MathUtils';
import { SlTpPriceType } from '@shared/utils/Enums/Constants';
import { DataCache } from '@shared/commons/DataCache';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { Serialize, DeserializeAsync } from '@shared/commons/cache/SavedOrders/SavedOrdersSerializer';
import { SavedOrdersController } from '@shared/commons/cache/SavedOrders/SavedOrdersController';
import { SavedOrderValidator } from '@shared/commons/cache/SavedOrders/SavedOrderValidator';
import { Position } from '@shared/commons/cache/Position';
import { TradingOrderStatus } from '@shared/commons/Trading/TradingOrderStatus';
import { messageBoxHandler } from '@shared/utils/AppHandlers';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { WDSettings } from '../settings/WDGeneralSettings';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { OrderTypeColumnRenderer } from '../elements/QuickTable/Renderers/OrderTypeColumnRenderer';
import { createTagDictionary, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class SavedOrdersPanel extends ApplicationPanelWithTable {
    constructor() {
        super();
        this.populateTableContextMenu = () => {
            const items = [];
            items.push({
                type: NewContextMenuItemType.Default,
                text: 'panel.savedOrders.menu.AddOrder',
                svgImage: 'add',
                event: this.onAddSavedOrderClicked
            });
            items.push({
                type: NewContextMenuItemType.Default,
                text: 'panel.savedOrders.menu.EditOrder',
                tag: SavedOrdersPanel.EDIT_ORDER,
                event: this.onEditSavedOrderClicked
            });
            items.push({
                type: NewContextMenuItemType.Default,
                text: 'panel.savedOrders.menu.RemoveOrder',
                tag: SavedOrdersPanel.REMOVE_ORDER,
                event: this.onRemoveSavedOrderClicked
            });
            items.push({
                type: NewContextMenuItemType.Default,
                text: 'panel.savedOrders.menu.RemoveAll',
                event: this.onRemoveAllSavedOrdersClicked
            });
            items.push({
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.View',
                svgImage: 'View',
                subitems: [
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.savedOrders.menu.ShowToolbar',
                        checked: super.get('isToolbarVisible'),
                        visible: !Resources.isHidden('panel.savedOrders.menu.ShowToolbar'),
                        canCheck: true,
                        event: this.onShowToolbarMenuItemCheckedChanged
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.savedOrders.menu.ShowTotals',
                        checked: false,
                        visible: !Resources.isHidden('contextMenu.Totals.visibility'),
                        canCheck: true,
                        event: super.ShowTotalsStateChange.bind(this)
                    }
                ]
            });
            items.push({ type: NewContextMenuItemType.Separator });
            items.push({
                type: NewContextMenuItemType.Default,
                text: 'panel.savedOrders.menu.SavedOrdersLists',
                subitems: this.Controls.editableCB.menuItems //TODO !!!!
            });
            super.AddSymbolInfoContextMenuItemIfNeed(items, true);
            super.AddExportContextMenuItem(items);
            this.menuTagDict = createTagDictionary(items);
            super.getQuickTable().setTableContextMenuItems(items);
        };
        this.addItem = (savedOrder) => {
            const table = super.getQuickTable();
            if (MathUtils.IsNullOrUndefined(table)) {
                return;
            }
            const rowsCount = table.rowsArray.filter((row) => !row.isGroupRow).length;
            const allowedRowsCount = MainWindowManager.Factory.GetThrottlingOperationValue(this.getType(), true).rowsNumber;
            if (allowedRowsCount !== -1 && rowsCount >= allowedRowsCount) {
                const title = Resources.getResource('workspace.information');
                const message = `${Resources.getResource('Rows.firstPart')} ${Resources.getResource(PanelLocKeys[this.getType()])} ${Resources.getResource('Rows.secondPart')}`;
                messageBoxHandler.Show(title, message, messageBoxHandler.msgType.Info, null, null, false, true);
                return;
            }
            const item = new SavedOrderItem(savedOrder, SessionSettings);
            item.subsribeOnUpdate(this.onUpdateItem);
            const row = table.AddItem(item);
            this.processEditRow(row);
            this.processErrorRow(row);
            this.updatePlaceButtonEnability();
            this.updateFilterByAccount();
        };
        this.updateFilterByAccount = () => {
            // TODO: make union with LinkedSystem.accLinkingActive ? me.enableAccountLinking() : me.disableAccountLinking() from ApplicationPanelWithTable.oncomplete
            if (super.OnAccountLinking()) {
                super.enableAccountLinking();
            }
            else {
                super.disableAccountLinking();
            }
        };
        this.removeItem = (item) => {
            item.unsubsribeOnUpdate(this.onUpdateItem);
            super.getQuickTable().RemoveItem(item.ItemId);
            this.updatePlaceButtonEnability();
        };
        this.clearTable = () => {
            const table = super.getQuickTable();
            if (MathUtils.IsNullOrUndefined(table)) {
                return;
            }
            const rows = table.rowsArray;
            for (let i = 0; i < rows.length; i++) {
                if (rows[i].isGroupRow) {
                    continue;
                }
                const item = rows[i].item;
                item.unsubsribeOnUpdate(this.onUpdateItem);
            }
            table.ClearRows();
            this.updatePlaceButtonEnability();
        };
        this.deserializeSavedOrdersAsync = (serializedData, signal) => __awaiter(this, void 0, void 0, function* () {
            const parsed = tryParseJson(serializedData);
            let serializedItems = [];
            if (Array.isArray(parsed)) {
                serializedItems = parsed;
            }
            else if (isValidString(serializedData)) {
                serializedItems = serializedData.split(';');
            }
            for (const serializedItem of serializedItems) {
                if (signal.aborted) {
                    return;
                }
                if (isValidString(serializedItem))
                    DeserializeAsync(serializedItem)
                        .then((savedOrder) => {
                        if (signal.aborted) {
                            return;
                        }
                        this.addItem(savedOrder);
                    })
                        .catch((error) => {
                        console.log(error);
                    });
            }
        });
        this.processEditRow = (row) => {
            const item = row.item;
            const savedOrder = row.item.savedOrder;
            for (let i = 0; i < row.cells.length; i++) {
                switch (i) {
                    case SavedOrderItem.CASH_COL_INDEX:
                        row.cells[i].ReadOnly = isNullOrUndefined(savedOrder.Cash) || savedOrder.Cash <= 0;
                        break;
                    case SavedOrderItem.QUANTITY_COL_INDEX:
                        row.cells[i].ReadOnly = savedOrder.Cash > 0;
                        break;
                    case SavedOrderItem.ACCOUNT_COL_INDEX:
                        row.cells[i].ReadOnly = DataCache.getNumberOfAccounts() === 1;
                        break;
                    case SavedOrderItem.PRODUCT_TYPE_COL_INDEX:
                        row.cells[i].ReadOnly = !item.isExistProductTypes;
                        break;
                    case SavedOrderItem.PRICE_COL_INDEX:
                        if (savedOrder.LinkedPriceType !== LinkedPriceType.None) {
                            row.cells[i].ReadOnly = true;
                        }
                        else {
                            row.cells[i].ReadOnly =
                                savedOrder.OrderType !== OrderType.Limit &&
                                    savedOrder.OrderType !== OrderType.StopLimit &&
                                    savedOrder.OrderType !== OrderType.OCO;
                        }
                        break;
                    case SavedOrderItem.STOP_PRICE_COL_INDEX:
                        if (savedOrder.LinkedPriceType !== LinkedPriceType.None) {
                            row.cells[i].ReadOnly = true;
                        }
                        else {
                            row.cells[i].ReadOnly =
                                savedOrder.OrderType !== OrderType.Stop &&
                                    savedOrder.OrderType !== OrderType.StopLimit &&
                                    savedOrder.OrderType !== OrderType.TrailingStop &&
                                    savedOrder.OrderType !== OrderType.OCO;
                        }
                        break;
                    case SavedOrderItem.LINK_COL_INDEX:
                        row.cells[i].ReadOnly =
                            savedOrder.OrderType !== OrderType.Limit && savedOrder.OrderType !== OrderType.Stop;
                        break;
                    case SavedOrderItem.LINKED_PRICE_OFFSET_COL_INDEX:
                        row.cells[i].ReadOnly =
                            savedOrder.LinkedPriceType === LinkedPriceType.None ||
                                (savedOrder.OrderType !== OrderType.Limit && savedOrder.OrderType !== OrderType.Stop);
                        break;
                    case SavedOrderItem.LEVERAGE_COL_INDEX:
                        row.cells[i].ReadOnly =
                            !isNullOrUndefined(savedOrder.Instrument) &&
                                !savedOrder.Instrument.isLeverageVisible(savedOrder.Account, savedOrder.ProductType);
                        break;
                    case SavedOrderItem.SL_PRICE_COL_INDEX:
                    case SavedOrderItem.SL_OFFSET_COL_INDEX:
                        row.cells[i].ReadOnly = isNaN(savedOrder.SlPrice) && !SavedOrderValidator.isAllowSl(savedOrder);
                        break;
                    case SavedOrderItem.SLL_PRICE_COL_INDEX:
                    case SavedOrderItem.SLL_OFFSET_COL_INDEX:
                        row.cells[i].ReadOnly =
                            isNaN(savedOrder.SllPrice) &&
                                (!SavedOrderValidator.isAllowSl(savedOrder) || !WDSettings.isUseStopLimitInsteadStop());
                        break;
                    case SavedOrderItem.TP_PRICE_COL_INDEX:
                    case SavedOrderItem.TP_OFFSET_COL_INDEX:
                        row.cells[i].ReadOnly = isNaN(savedOrder.TpPrice) && !SavedOrderValidator.isAllowTp(savedOrder);
                        break;
                }
            }
        };
        this.processErrorRow = (row) => {
            var _a;
            const savedOrderItem = row.item;
            for (let i = 0; i < row.cells.length; i++) {
                const savedOrder = savedOrderItem.savedOrder;
                let validationResult = { isValid: true, error: '' };
                switch (i) {
                    case SavedOrderItem.ACCOUNT_COL_INDEX:
                        validationResult = SavedOrderValidator.isValidAccount(savedOrder);
                        break;
                    case SavedOrderItem.INSTRUMENT_COL_INDEX:
                        validationResult = SavedOrderValidator.isValidInstrument(savedOrder);
                        break;
                    case SavedOrderItem.CASH_COL_INDEX:
                        validationResult = SavedOrderValidator.isValidCash(savedOrder);
                        break;
                    case SavedOrderItem.QUANTITY_COL_INDEX:
                    case SavedOrderItem.DISCLOSED_QUANTITY_COL_INDEX:
                        validationResult = SavedOrderValidator.isValidQuantity(savedOrder);
                        break;
                    case SavedOrderItem.PRICE_COL_INDEX:
                        validationResult = SavedOrderValidator.isValidPrice(savedOrder);
                        break;
                    case SavedOrderItem.STOP_PRICE_COL_INDEX:
                        validationResult = SavedOrderValidator.isValidStopPrice(savedOrder);
                        break;
                    case SavedOrderItem.SL_PRICE_COL_INDEX:
                    case SavedOrderItem.SL_OFFSET_COL_INDEX:
                        validationResult = SavedOrderValidator.isValidSl(savedOrder);
                        break;
                    case SavedOrderItem.SLL_PRICE_COL_INDEX:
                    case SavedOrderItem.SLL_OFFSET_COL_INDEX:
                        validationResult = SavedOrderValidator.isValidSll(savedOrder);
                        break;
                    case SavedOrderItem.TP_PRICE_COL_INDEX:
                    case SavedOrderItem.TP_OFFSET_COL_INDEX:
                        validationResult = SavedOrderValidator.isValidTp(savedOrder);
                        break;
                }
                row.cells[i].isError = !validationResult.isValid;
                (_a = row.item) === null || _a === void 0 ? void 0 : _a.SetTooltipKey(i, isValidString(validationResult.error) ? validationResult.error : '');
            }
        };
        this.onUpdateItem = (item) => {
            const row = super.getQuickTable().getItemById(item.ItemId);
            if (!MathUtils.IsNullOrUndefined(row)) {
                this.processErrorRow(row);
            }
        };
        this.onSelectedListChanged = (serializedData) => {
            var _a;
            const table = super.getQuickTable();
            if (MathUtils.IsNullOrUndefined(table)) {
                return;
            }
            (_a = this.abortController) === null || _a === void 0 ? void 0 : _a.abort();
            this.abortController = new AbortController();
            this.clearTable();
            super.updatePanelHeader();
            this.deserializeSavedOrdersAsync(serializedData, this.abortController.signal).catch((error) => {
                console.log(error);
            });
        };
        this.onAddSavedOrderClicked = () => {
            var _a;
            this._orderEntry = (_a = MainWindowManager.Factory) === null || _a === void 0 ? void 0 : _a.addPanel(PanelNames.AdvancedOrderEntry, null, this.onOpenOrderEntryPanel);
        };
        this.onEditSavedOrderClicked = () => {
            const selectedItems = super.getQuickTable().getSelectedTableItems();
            if (selectedItems.length > 0) {
                this._savedOrderForEdit = selectedItems[0].savedOrder;
                this.onAddSavedOrderClicked();
            }
        };
        this.onRemoveSavedOrderClicked = () => {
            super.getQuickTable().RemoveSelectedItems();
            this.saveList();
        };
        this.onRemoveAllSavedOrdersClicked = () => {
            super.getQuickTable().ClearRows();
            this.saveList();
        };
        this.onOpenOrderEntryPanel = (panel) => {
            const isEdit = !isNullOrUndefined(this._savedOrderForEdit);
            panel.OnClose.Subscribe(this.onCloseOrderEntryPanel, this);
            panel.set({
                isCreateOrderParmeters: true,
                placeButtonAdditionalClass: 'alertStyle',
                placeButtonLocalizationKey: isEdit ? 'panel.newOrderEntry.SaveParams' : 'panel.newOrderEntry.AddSavedOrder',
                showCustomFullscreenCloud: true
            });
            if (isEdit) {
                panel.fillByOrderEdit(SavedOrdersController.createOrderEdit(this._savedOrderForEdit, true, PlacedFrom.WEB_SAVED_ORDERS));
            }
            panel.NewParametrsHandler = this.onCreateSavedOrder;
        };
        this.onCloseOrderEntryPanel = (panel) => {
            panel.OnClose.UnSubscribe(this.onCloseOrderEntryPanel, this);
            panel.NewParametrsHandler = null;
            this._orderEntry = undefined;
            this._savedOrderForEdit = undefined;
        };
        this.onCreateSavedOrder = (orderEdit) => {
            if (!isNullOrUndefined(this._savedOrderForEdit)) {
                SavedOrdersController.updateByOrderEdit(this._savedOrderForEdit, orderEdit);
            }
            else {
                const savedOrder = SavedOrdersController.createSavedOrder(orderEdit);
                this.addItem(savedOrder);
            }
            this.saveList();
            orderEdit.dispose();
        };
        this.onTableRowButtonClicked = (args) => __awaiter(this, void 0, void 0, function* () {
            const tableItem = args.row.item;
            switch (args.realColumnIndex) {
                case SavedOrderItem.REMOVE_ORDER_COL_INDEX:
                    this.removeItem(tableItem);
                    break;
                case SavedOrderItem.SUBMIT_COL_INDEX:
                    yield SavedOrdersController.placeOrders([tableItem.savedOrder], PlacedFrom.WEB_SAVED_ORDERS, BaseSettings.confirmOrderSending);
                    break;
                default:
                    return yield Promise.resolve(true);
            }
            this.saveList();
        });
        this.onAfterEditItem = (data) => {
            var _a, _b, _c;
            let needResort = false;
            const tableItem = data.row.item;
            const savedOrder = tableItem.savedOrder;
            switch (data.realColumnIndex) {
                case SavedOrderItem.ACTIVE_COL_INDEX:
                    savedOrder.Active = !savedOrder.Active;
                    break;
                case SavedOrderItem.ACCOUNT_COL_INDEX:
                    savedOrder.Account = data.newValue;
                    break;
                case SavedOrderItem.INSTRUMENT_COL_INDEX:
                    savedOrder.Instrument = data.newValue;
                    savedOrder.setDefaultPrices();
                    savedOrder.setLinkedPrice();
                    break;
                case SavedOrderItem.OPERATION_COL_INDEX:
                    savedOrder.Operation = data.newValue;
                    break;
                case SavedOrderItem.ORDER_TYPE_COL_INDEX:
                    savedOrder.OrderType = data.newValue;
                    savedOrder.setDefaultPrices();
                    savedOrder.setLinkedPrice();
                    break;
                case SavedOrderItem.PRODUCT_TYPE_COL_INDEX:
                    savedOrder.ProductType = data.newValue;
                    break;
                case SavedOrderItem.TIF_COL_INDEX:
                    savedOrder.TIF = data.newValue.type;
                    savedOrder.TIFExpiration = data.newValue.expirationTime;
                    break;
                case SavedOrderItem.CASH_COL_INDEX:
                    savedOrder.Cash = data.newValue;
                    break;
                case SavedOrderItem.QUANTITY_COL_INDEX:
                    savedOrder.QuantityLots = Quantity.convertAmountToLots(data.newValue, tableItem.savedOrder.Instrument);
                    break;
                case SavedOrderItem.DISCLOSED_QUANTITY_COL_INDEX:
                    savedOrder.DisclosedQuantityLots = Quantity.convertAmountToLots(data.newValue, tableItem.savedOrder.Instrument);
                    break;
                case SavedOrderItem.PRICE_COL_INDEX:
                    savedOrder.Price = data.newValue;
                    break;
                case SavedOrderItem.STOP_PRICE_COL_INDEX:
                    if (savedOrder.OrderType === OrderType.TrailingStop) {
                        savedOrder.StopPrice = SlTpUtils.toRealValue(data.newValue, savedOrder.Instrument, SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(savedOrder.Instrument, BaseSettings.offsetType));
                    }
                    else {
                        savedOrder.StopPrice = data.newValue;
                    }
                    break;
                case SavedOrderItem.TRAILING_STOP_COL_INDEX:
                    if (savedOrder.SLTPHolder.isTrailingStop()) {
                        savedOrder.SLTPHolder.StopLossPriceType = SlTpPriceType.Offset;
                    }
                    else {
                        savedOrder.SLTPHolder.StopLossPriceType = SlTpPriceType.TrOffset;
                    }
                    break;
                case SavedOrderItem.SL_PRICE_COL_INDEX:
                    savedOrder.SLTPHolder.StopLossLimitPriceValue = savedOrder.SllPrice;
                    savedOrder.SLTPHolder.StopLossPriceType = SlTpPriceType.Absolute;
                    savedOrder.SLTPHolder.StopLossPriceValue = data.newValue === 0 ? NaN : data.newValue;
                    if (BaseSettings.isUseStopLimitInsteadStop() &&
                        savedOrder.SLTPHolder.isSllEmpty() &&
                        !savedOrder.SLTPHolder.isTrailingStop()) {
                        savedOrder.SLTPHolder.StopLossLimitPriceValue = savedOrder.Instrument.calculatePrice(savedOrder.SlPrice, BaseSettings.limitOffsetTicks, true);
                    }
                    break;
                case SavedOrderItem.SLL_PRICE_COL_INDEX:
                    savedOrder.SLTPHolder.StopLossPriceValue = savedOrder.SlPrice;
                    savedOrder.SLTPHolder.StopLossPriceType = SlTpPriceType.Absolute;
                    savedOrder.SLTPHolder.StopLossLimitPriceValue = data.newValue === 0 ? NaN : data.newValue;
                    break;
                case SavedOrderItem.TP_PRICE_COL_INDEX:
                    savedOrder.SLTPHolder.TakeProfitPriceType = SlTpPriceType.Absolute;
                    savedOrder.SLTPHolder.TakeProfitPriceValue = data.newValue === 0 ? NaN : data.newValue;
                    break;
                case SavedOrderItem.SL_OFFSET_COL_INDEX:
                    if (data.newValue !== 0) {
                        savedOrder.SLTPHolder.StopLossLimitPriceValue = savedOrder.SLTPHolder.isTrailingStop()
                            ? NaN
                            : savedOrder.SllOffset;
                        const slVisualOffset = (_a = SlTpUtils.getSLVisualOffsetType(savedOrder.Instrument, savedOrder.SLTPHolder, BaseSettings.offsetType)) !== null && _a !== void 0 ? _a : SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(savedOrder.Instrument, BaseSettings.offsetType);
                        savedOrder.SLTPHolder.StopLossPriceType = SlTpUtils.getSLTPPriceType(slVisualOffset);
                        savedOrder.SLTPHolder.StopLossPriceValue = SlTpUtils.toRealValue(data.newValue, savedOrder.Instrument, slVisualOffset);
                        if (BaseSettings.isUseStopLimitInsteadStop() &&
                            savedOrder.SLTPHolder.isSllEmpty() &&
                            !savedOrder.SLTPHolder.isTrailingStop()) {
                            savedOrder.SLTPHolder.StopLossLimitPriceValue = BaseSettings.limitOffsetTicks;
                        }
                    }
                    else {
                        savedOrder.SLTPHolder.StopLossPriceType = SlTpPriceType.Absolute;
                        savedOrder.SLTPHolder.StopLossPriceValue = NaN;
                        savedOrder.SLTPHolder.StopLossLimitPriceValue = NaN;
                    }
                    break;
                case SavedOrderItem.SLL_OFFSET_COL_INDEX: {
                    savedOrder.SLTPHolder.StopLossPriceValue = savedOrder.SlOffset;
                    const sllVisualOffsetType = (_b = SlTpUtils.getSLVisualOffsetType(savedOrder.Instrument, savedOrder.SLTPHolder, BaseSettings.offsetType)) !== null && _b !== void 0 ? _b : SlTpUtils.getDefaultVisualOffsetType(savedOrder.Instrument, BaseSettings.offsetType);
                    savedOrder.SLTPHolder.StopLossPriceType = SlTpUtils.getSLTPPriceType(sllVisualOffsetType);
                    savedOrder.SLTPHolder.StopLossLimitPriceValue = SlTpUtils.toRealValue(data.newValue, savedOrder.Instrument, sllVisualOffsetType);
                    break;
                }
                case SavedOrderItem.TP_OFFSET_COL_INDEX:
                    if (data.newValue !== 0) {
                        const tpVisualOffsetType = (_c = SlTpUtils.getTPVisualOffsetType(savedOrder.Instrument, savedOrder.SLTPHolder, BaseSettings.offsetType)) !== null && _c !== void 0 ? _c : SlTpUtils.getDefaultVisualOffsetType(savedOrder.Instrument, BaseSettings.offsetType);
                        savedOrder.SLTPHolder.TakeProfitPriceType = SlTpUtils.getSLTPPriceType(tpVisualOffsetType);
                        savedOrder.SLTPHolder.TakeProfitPriceValue = SlTpUtils.toRealValue(data.newValue, savedOrder.Instrument, tpVisualOffsetType);
                    }
                    else {
                        savedOrder.SLTPHolder.TakeProfitPriceType = SlTpPriceType.Absolute;
                        savedOrder.SLTPHolder.TakeProfitPriceValue = NaN;
                    }
                    break;
                case SavedOrderItem.LINKED_PRICE_OFFSET_COL_INDEX:
                    savedOrder.LinkedPriceOffset = SlTpUtils.toRealValue(data.newValue, savedOrder.Instrument, SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(savedOrder.Instrument, BaseSettings.offsetType));
                    savedOrder.setLinkedPrice();
                    break;
                case SavedOrderItem.LINK_COL_INDEX:
                    savedOrder.LinkedPriceType = data.newValue;
                    savedOrder.setLinkedPrice();
                    break;
                case SavedOrderItem.LEVERAGE_COL_INDEX:
                    savedOrder.Leverage = data.newValue;
                    needResort = true;
                    break;
            }
            this.processEditRow(data.row);
            this.processErrorRow(data.row);
            if (needResort) {
                super.getQuickTable().updateRowsCellsValues(true);
            }
            else {
                data.row.FillRowByItem(tableItem, true);
            }
            this.updatePlaceButtonEnability();
            this.quickTableRactive.redraw();
            this.saveList();
        };
        this.onPlaceActiveButtonClick = () => __awaiter(this, void 0, void 0, function* () {
            const rows = this.quickTableRactive.quickTable.rowsArray;
            const ordersForPlace = [];
            for (let i = 0; i < rows.length; i++) {
                if (rows[i].isGroupRow) {
                    continue;
                }
                const item = rows[i].item;
                if (item.savedOrder.Active) {
                    ordersForPlace.push(item.savedOrder);
                }
            }
            yield SavedOrdersController.placeOrders(ordersForPlace, PlacedFrom.WEB_SAVED_ORDERS, BaseSettings.confirmOrderSending);
            this.saveList();
        });
        this.onProcessOpenOrder = (orderOrPosition) => {
            const comment = orderOrPosition.Comment;
            if (comment !== null && comment.startsWith('SavedOrder.')) {
                const id = comment.substring('SavedOrder.'.length);
                const rows = super.getQuickTable().rowsArray;
                for (let i = 0; i < rows.length; i++) {
                    if (rows[i].isGroupRow) {
                        continue;
                    }
                    const savedOrder = rows[i].item.savedOrder;
                    if (savedOrder.Id === id) {
                        if (orderOrPosition instanceof Position) {
                            savedOrder.OpenedId = orderOrPosition.PositionId;
                            savedOrder.Status = TradingOrderStatus.Filled;
                        }
                        else {
                            savedOrder.OpenedId = orderOrPosition.OrderNumber;
                            savedOrder.Status = TradingOrderStatus.Opened;
                        }
                        this.saveList();
                        break;
                    }
                }
            }
        };
        this.onShowToolbarMenuItemCheckedChanged = (menuItem) => {
            void this.set('isToolbarVisible', menuItem.checked);
        };
        this.onToolbarVisibleChanged = (newValue, oldValue) => {
            super.layoutTable();
        };
        this.saveList = () => {
            const editableCB = this.Controls.editableCB;
            if (isNullOrUndefined(editableCB)) {
                return;
            }
            const selectedItem = editableCB.get('selectedItem');
            if (isNullOrUndefined(selectedItem)) {
                return;
            }
            const rows = super.getQuickTable().rowsArray;
            const selectedList = editableCB.valueOfLists.find((list) => list.name === selectedItem.value);
            if (isNullOrUndefined(selectedList))
                return;
            const items = [];
            for (const row of rows)
                if (!row.isGroupRow)
                    items.push(Serialize(row.item.savedOrder));
            selectedList.itemsStr = JSON.stringify(items);
            editableCB.updateListsValue(null, false);
        };
        this.resetList = () => {
            const editableCB = this.Controls.editableCB;
            if (MathUtils.IsNullOrUndefined(editableCB)) {
                return;
            }
            editableCB.resetList();
        };
        this.updatePlaceButtonEnability = () => {
            const rows = super.getQuickTable().rowsArray;
            let enabled = false;
            for (let i = 0; i < rows.length; i++) {
                if (rows[i].isGroupRow) {
                    continue;
                }
                const item = rows[i].item;
                if (item.savedOrder.Active) {
                    enabled = true;
                    break;
                }
            }
            void this.set('isEnabledPlaceButton', enabled);
        };
        this.headerLocaleKey = 'panel.savedOrders';
    }
    getType() {
        return PanelNames.SavedOrdersPanel;
    }
    repopulate() {
        super.repopulate();
        this.resetList();
    }
    layoutTable() {
        super.layoutTable();
    }
    TickAsync() {
        super.TickAsync();
    }
    localize() {
        super.localize();
        const placeButtonText = Resources.getResource('panel.savedOrders.placeButtonText');
        void this.set('placeButtonText', placeButtonText);
    }
    updateSettings() {
        super.updateSettings();
        const rows = super.getQuickTable().rowsArray;
        for (let i = 0; i < rows.length; i++) {
            if (rows[i].isGroupRow) {
                continue;
            }
            const item = rows[i].item;
            item.updateEditingInfo();
            this.processEditRow(rows[i]);
        }
    }
    oncomplete() {
        super.oncomplete();
        super.on('onAddButtonClick', this.onAddSavedOrderClicked);
        super.on('onPlaceButtonClick', this.onPlaceActiveButtonClick);
        super.observe('isToolbarVisible', this.onToolbarVisibleChanged);
        const table = super.getQuickTable();
        if (MathUtils.IsNullOrUndefined(table)) {
            return;
        }
        table.columns[SavedOrderItem.ORDER_TYPE_COL_INDEX].renderer = new OrderTypeColumnRenderer();
        this.populateTableContextMenu();
        this.Controls.editableCB.OnSelectList.Subscribe(this.onSelectedListChanged, this);
        table.OnPaintedPictureButtonClick.Subscribe(this.onTableRowButtonClicked, this);
        table.AfterEditItem.Subscribe(this.onAfterEditItem, this);
        this.Controls.editableCB.resetList();
        DataCache.OnAddOrder.Subscribe(this.onProcessOpenOrder, this);
        DataCache.OnAddPosition.Subscribe(this.onProcessOpenOrder, this);
    }
    dispose() {
        DataCache.OnAddOrder.UnSubscribe(this.onProcessOpenOrder, this);
        DataCache.OnAddPosition.UnSubscribe(this.onProcessOpenOrder, this);
        super.off('onAddButtonClick', this.onAddSavedOrderClicked);
        super.off('onPlaceButtonClick', this.onPlaceActiveButtonClick);
        const table = super.getQuickTable();
        if (MathUtils.IsNullOrUndefined(table)) {
            return;
        }
        this.Controls.editableCB.OnSelectList.UnSubscribe(this.onSelectedListChanged, this);
        table.OnPaintedPictureButtonClick.UnSubscribe(this.onTableRowButtonClicked, this);
        table.AfterEditItem.UnSubscribe(this.onAfterEditItem, this);
        super.dispose();
    }
    preparePopup() {
        var _a;
        super.preparePopup();
        const qt = super.getQuickTable();
        const selectedItems = (_a = qt === null || qt === void 0 ? void 0 : qt.getSelectedTableItems()) !== null && _a !== void 0 ? _a : [];
        for (let i = 0; i < qt.tableContextMenuItems.length; i++) {
            const menuItem = qt.tableContextMenuItems[i];
            switch (menuItem.tag) {
                case SavedOrdersPanel.EDIT_ORDER:
                    menuItem.enabled = selectedItems.length === 1;
                    if (!menuItem.enabled) {
                        menuItem.tooltip = Resources.getResource(selectedItems.length === 0
                            ? 'panel.orders.menu.NoSymbolInfoDisabled.tt'
                            : 'panel.orders.menu.MoreOneSymbolInfoDisabled.tt');
                    }
                    else {
                        menuItem.tooltip = '';
                    }
                    menuItem.svgImage = menuItem.enabled ? '' : 'menuError';
                    break;
                case SavedOrdersPanel.REMOVE_ORDER:
                    menuItem.enabled = selectedItems.length > 0;
                    menuItem.tooltip = menuItem.enabled
                        ? ''
                        : Resources.getResource('panel.orders.menu.NoSymbolInfoDisabled.tt');
                    menuItem.svgImage = menuItem.enabled ? '' : 'menuError';
                    break;
            }
        }
    }
    /**
     * Sets the default display order of columns in the table based on a saved order.
     * Iterates over each column in the table and assigns the display index
     * using the saved menu index for each column's header text.
     *
     * @param {QuickTable} table - The table object containing the columns to update.
     * @param {Array} table.columns - An array of column objects in the table.
     * @param {Object} table.columns[].headerText - The header text of each column used to retrieve the saved menu index.
     * @param {number} table.columns[].displayedIndex - The index that determines the display order of the column.
     */
    SetColumnsDefaultDisplayIndex(table) {
        table.columns.forEach((column, index) => {
            const menuIndex = SavedOrderItem.GetMenuIndex(column.headerText);
            table.columns[index].displayedIndex = menuIndex;
        });
    }
}
SavedOrdersPanel.EDIT_ORDER = 'EDIT_ORDER';
SavedOrdersPanel.REMOVE_ORDER = 'REMOVE_ORDER';
ApplicationPanelWithTable.extendWith(SavedOrdersPanel, {
    Name: 'SavedOrdersPanel',
    data: function () {
        return {
            isToolbarVisible: true,
            isEnabledAddButton: true,
            isEnabledCombobox: true,
            isEnabledPlaceButton: true,
            isOeOpened: false,
            editableCBtooltip: '',
            placeButtonText: ''
        };
    },
    partials: {
        bodyPartial: SavedOrdersPanelTopTemplate
    },
    headerLocaleKey: 'panel.savedOrders'
});
